import * as React from 'react';
import { useMediaQuery } from 'react-responsive';

// TODO: why isn't this a circular dependency? Does TS know not to reimport this file?
import {
  FeatureOverrideProvider,
  ExperimentProvider,
  StringsProvider,
  ThemeProvider,
  useAnalytics,
  useConfig,
  useContractContext,
  useFeatures,
  usePostMessageHandler,
  IStringsContext,
} from 'src/providers';

import { useCustomText } from 'src/hooks/useCustomText';
import { useInitializedEvent } from 'src/hooks/useInitializedEvent';
import { useCurrencyFormatter } from 'src/hooks/useCurrencyFormatter';

import { ModalType } from 'src/types/Events';

const VERSION = '1.0.0';

interface ITheme {
  partnerLogo: string | null;
  shopName: string;
  showXCoverBranding: boolean | null;
}

export interface IModalSettingsContext {
  isModalOpen: boolean;
  strings: Partial<IStringsContext>;
  theme: ITheme;
  modalType: ModalType;
  openedBy: 'cart' | string | undefined;
}

export const ModalSettingsContext = React.createContext<IModalSettingsContext | null>(null);

const hasCustomFaqs = (customText: ReturnType<typeof useConfig>['customText']) => (
  Object
    .values(customText)
    .filter((obj) => typeof obj === 'object' && obj.answer && obj.question).length > 0
);

export const ModalSettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    customText,
    displayName,
    logo,
    phrases,
    showXCoverBranding,
  } = useConfig();
  const { track } = useAnalytics();
  const { contracts, selectedContract } = useContractContext();
  const { affirm, showPriceInButton } = useFeatures();
  const formatCurrency = useCurrencyFormatter();

  const [modalType, setModalType] = React.useState<ModalType>('response');
  const [openedBy, setOpenedBy] = React.useState<string | undefined>(undefined);
  const [useUpdateFlow, setUseUpdateFlow] = React.useState(false);

  usePostMessageHandler('openModal', (e) => {
    const openModalType = e.detail.modalType || 'response';
    setModalType(openModalType);
    setOpenedBy(e.detail.source || undefined);

    // NOTE: only used by Checkbox Cart experiment for now
    setUseUpdateFlow(e.detail.useUpdateFlow || false);

    track('impression', {
      ctaVersion: '1.0.0',
      type: 'modal',
      modalType: openModalType,
      paymentOption: affirm ? 'affirm' : null,
    });
  });

  const [theme, setTheme] = React.useState<ITheme>({
    partnerLogo: null,
    shopName: '',
    showXCoverBranding: false,
  });

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  usePostMessageHandler('openModal', React.useCallback(() => setIsModalOpen(true), [setIsModalOpen]));
  usePostMessageHandler('closeModal', React.useCallback(() => setIsModalOpen(false), [setIsModalOpen]));

  const initialized = useInitializedEvent({
    frame: 'modal',
    version: VERSION
  });

  React.useEffect(() => {
    setTheme((prevTheme) => ({
      ...prevTheme,
      partnerLogo: logo,
      shopName: displayName,
      showXCoverBranding: showXCoverBranding,
    }));
  }, [displayName, logo, showXCoverBranding, setTheme]);

  const faqs = React.useMemo(() => ({
    faq1_answer: customText.faq1.answer || phrases['faq.01.answer'],
    faq1_question: customText.faq1.question || phrases['faq.01.question'],
    faq2_answer: customText.faq2.answer || phrases['faq.02.answer'],
    faq2_question: customText.faq2.question || phrases['faq.02.question'],
    faq3_answer: customText.faq3.answer || phrases['faq.03.answer'],
    faq3_question: customText.faq3.question || phrases['faq.03.question'],
    faq4_answer: customText.faq4.answer || phrases['faq.04.answer'],
    faq4_question: customText.faq4.question || phrases['faq.04.question'],
    faq5_answer: customText.faq5.answer || phrases['faq.05.answer'],
    faq5_question: customText.faq5.question || phrases['faq.05.question'],
    faq6_answer: hasCustomFaqs(customText) ? undefined : phrases['faq.gift.answer'],
    faq6_question: hasCustomFaqs(customText) ? undefined : phrases['faq.gift.question'],
  }), [customText, phrases, hasCustomFaqs]);

  const addButtonLabel = React.useMemo(() => {
    if (useUpdateFlow) {
      return phrases['modal.addButtonUpdate'];
    } else if (showPriceInButton && selectedContract?.price) {
      const price = formatCurrency(+selectedContract.price);
      return phrases['modal.addButtonPrice'].replace('{price}', price);
    } else {
      return phrases['modal.addButton'];
    }
  }, [showPriceInButton, selectedContract, useUpdateFlow, phrases]);

  const bulletPoints = React.useMemo(() => {
    if (customText.bulletPoints) {
      // Custom text bullet points take precedence over all other sources
      return customText.bulletPoints.split('|');
    } else if ((contracts[0]?.bulletPoints || []).length) {
      // If no custom text, set bullet points to what's on the contract
      return contracts[0]?.bulletPoints;
    } else {
      // Fall back to bullet points from Translation table
      return phrases['modal.bullets'].split('|');
    }
  }, [customText.bulletPoints, phrases, contracts]);

  const isMobile = useMediaQuery({ query: '(max-width: 600px)' });
  const strings: Partial<IStringsContext> = {
    addProtectionButtonLabel: useCustomText('addButton', addButtonLabel),
    bestValueLabel: phrases['modal.bestValue'],
    bullets: bulletPoints,
    bulletsHeadline: useCustomText('bulletPointsHeadline', phrases['modal.bulletsHeadline']),
    declineButtonLabel: useCustomText('declineButton', phrases['modal.declineButton']),
    faqBackButton: phrases['faq.backButton'],
    faqButton: phrases['modal.faqButton'],
    faqHeadline: phrases['faq.headline'],
    headline: useCustomText('modalHeadline', phrases['modal.headline']),
    learnMoreLink: phrases['shared.learnMoreLink'],
    mostPopularLabel: phrases['modal.mostPopular'],
    termLabel: isMobile ? phrases['shared.termPlural'] : phrases['shared.termPlan'],
    termsAndConditionsLink: phrases['modal.termsAndConditionsButton'],
    ...faqs,
  };

  const value: IModalSettingsContext = {
    isModalOpen,
    theme,
    strings,
    modalType,
    openedBy,
  };

  if (!initialized) {
    return null;
  }

  return (
    <ModalSettingsContext.Provider value={value}>
      <FeatureOverrideProvider invertColors={false}>
        <ThemeProvider>
          <StringsProvider {...strings}>
            <ExperimentProvider>
              {children}
            </ExperimentProvider>
          </StringsProvider>
        </ThemeProvider>
      </FeatureOverrideProvider>
    </ModalSettingsContext.Provider>
  );
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const useModalSettingsContext = () => React.useContext(ModalSettingsContext)!;
